/**
 * How the face matcher's suggestions are grouped on screen (ADR-0040).
 *
 * Every suggestion carries its raw similarity; the API decides which of them
 * earn the *suggested match* label and names the conditions the others miss.
 * The UI never re-derives that rule — it only groups by the verdict and gives
 * each group its tone, so the thresholds live in one place on the server.
 */

/** Suggested matches first, then every other suggestion with its number only. */
export const MATCH_TIERS = ['suggested', 'other'] as const

export type MatchTier = (typeof MATCH_TIERS)[number]

/** The label's conditions, in the order the match modal lists them. */
export const MATCH_CONDITIONS = ['similarity', 'quality', 'agreement', 'margin'] as const

export type MatchCondition = (typeof MATCH_CONDITIONS)[number]

/** What the API says about one suggestion's label, with every figure formatted. */
export interface MatchEvidenceView {
  suggestedMatch: boolean
  /** Second-best reference similarity; null when no second reference answered. */
  scoreSecond: string | null
  /** Best score of another identity on the same face; null when there is none. */
  rivalScore: string | null
  referenceCount: number
  /** The label's conditions this suggestion misses, as the API names them. */
  unmetConditions: string[]
}

/**
 * The tier's tone: the badge variant, and inside the star the node's edge, the
 * dashed ring, the spoke and the legend's dot, as token classes. Only a
 * suggested match gets a colour — the rest stay neutral, so the colour itself
 * says where to look first.
 */
export interface TierTone {
  badge: 'success' | 'muted'
  node: string
  ring: string
  edge: string
  dot: string
}

export const TIER_TONE: Record<MatchTier, TierTone> = {
  suggested: {
    badge: 'success',
    node: 'stroke-success',
    ring: 'stroke-success/40',
    edge: 'stroke-success/70',
    dot: 'bg-success',
  },
  other: {
    badge: 'muted',
    node: 'stroke-muted-foreground/60',
    ring: 'stroke-muted-foreground/30',
    edge: 'stroke-muted-foreground/40',
    dot: 'bg-muted-foreground',
  },
}
